import { useState, type DOMAttributes } from 'react'

import { useDrag } from '@use-gesture/react'

/**
 * Жест «смахнуть, чтобы закрыть» — общий хук нового клиента, перенос хука
 * старого (`frontend/src/shared/hooks/useSwipeDismiss.ts`, CLAUDE.md §7). Один
 * на все пакеты (UA1c): 1С и AQSI берут его отсюда, своих копий не держат.
 * Жест никогда не единственный способ: у каждого есть кнопка-дублёр.
 *
 * Почему пакет, а не свои `onTouchStart/Move/End`. Прежняя ручная реализация
 * (нижний лист) слушала ТОЛЬКО события `touch`: стилус и мышь мимо неё, а
 * «смахнул и остановился» отличить от «просто держит палец» она не умела —
 * скорости у неё не было, решение принималось по одному пройденному
 * расстоянию. `@use-gesture` работает на pointer-событиях, то есть одинаково
 * видит палец, стилус и мышь, даёт скорость и направление и сам отменяет жест,
 * когда пользователь на самом деле прокручивает содержимое.
 *
 * Закрытие срабатывает по ЛЮБОМУ из двух признаков: прошли порог расстояния
 * (медленное осознанное движение) или превысили порог скорости (короткий резкий
 * flick). Один порог расстояния делал бы быстрый смах невозможным, один порог
 * скорости — медленный.
 */
export type SwipeAxis = 'down' | 'right' | 'left'

export interface SwipeDismissOptions {
  /**
   * Куда смахивают: нижний лист — вниз, боковая панель — вправо, строка списка —
   * влево (привычное «смахнуть и удалить»).
   */
  axis: SwipeAxis
  onDismiss: () => void
  /** Пройденное расстояние, после которого поверхность закрывается, px. */
  distance?: number
  /** Скорость короткого смаха, px/мс. */
  velocity?: number
  /** Жест выключен — например, поверхность не в модальном режиме. */
  disabled?: boolean
}

const DEFAULT_DISTANCE = 96
const DEFAULT_VELOCITY = 0.5

export interface SwipeDismissResult {
  /**
   * Пропсы для зоны захвата: `<div {...swipe.bind()} />`.
   *
   * Тип сужен до «функция, возвращающая пропсы DOM»: собственный тип пакета
   * объявлен как `(...args: any[])`, и с ним каждый вызов `bind()` в разметке
   * становится ошибкой `no-unsafe-call`. Берём общий `DOMAttributes` React, а
   * не внутренний тип пакета: тот лежит по пути `dist/declarations/...`,
   * который пакет не обещает сохранять.
   */
  bind: () => DOMAttributes<Element>
  /** Сдвиг поверхности за пальцем, px. Ноль — жеста нет. */
  offset: number
  /** Готовый стиль поверхности; `undefined`, пока её не тянут. */
  style: { transform: string } | undefined
}

export function useSwipeDismiss({
  axis,
  onDismiss,
  distance = DEFAULT_DISTANCE,
  velocity = DEFAULT_VELOCITY,
  disabled = false,
}: SwipeDismissOptions): SwipeDismissResult {
  const [offset, setOffset] = useState(0)

  const bind = useDrag(
    ({ down, movement: [mx, my], velocity: [vx, vy], direction: [dx, dy] }) => {
      // Влево — то же движение с обратным знаком: дальше по коду «сдвиг»
      // всегда положительное число, сколько бы ни прошли.
      const sign = axis === 'left' ? -1 : 1
      const moved = axis === 'down' ? my : mx * sign
      const speed = axis === 'down' ? vy : vx
      const towards = (axis === 'down' ? dy : dx) * sign
      // Тянем только в сторону закрытия: обратное движение поверхность не
      // растягивает.
      const shift = moved > 0 ? moved : 0

      if (down) {
        setOffset(shift)
        return
      }
      // Жест уже завершён — отменять его нечем и незачем: здесь только решение,
      // закрывать или отпустить поверхность назад.
      setOffset(0)
      if (shift > distance || (speed > velocity && towards > 0)) onDismiss()
    },
    {
      enabled: !disabled,
      axis: axis === 'down' ? 'y' : 'x',
      // Тап (нажал-отпустил без движения) жестом не считается: иначе обычное
      // нажатие на «грабер» закрывало бы поверхность.
      filterTaps: true,
    },
  )

  return {
    bind,
    offset,
    style: offset > 0
      ? {
        transform: axis === 'down'
          ? `translateY(${offset}px)`
          : `translateX(${axis === 'left' ? -offset : offset}px)`,
      }
      : undefined,
  }
}
